// Donate.js - Complete Version with Date Slot Availability + iOS Donate Params
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Donate.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService, { getReceiptUrl, BASE_URL } from '../services/api';

// --- Main Donate Component ---
export default function Donate() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const donationTypeFromUrl = queryParams.get('type');

  // ✅ iOS donate params (from external browser redirect)
  const iosParams = {
    donorName:         queryParams.get('donor_name') || '',
    donorEmail:        queryParams.get('donor_email') || '',
    donorPhone:        queryParams.get('donor_phone') || '',
    donorAddress:      queryParams.get('donor_address') || '',
    donorPan:          queryParams.get('donor_pan') || '',
    donorOrganization: queryParams.get('donor_organization') || '',
    amount:            queryParams.get('amount') || '',
    purpose:           queryParams.get('purpose') || '',
    purposeId:         queryParams.get('purpose_id') || '',
    notes:             queryParams.get('notes') || '',
    donationType:      queryParams.get('donation_type') || '',
    source:            queryParams.get('source') || '',
    donorId:           queryParams.get('donor_id') || '',
  };

  // Show the iOS details card only if data is present
  const showIOSDetails = iosParams.source === 'ios_app' || iosParams.donorPhone !== '';

  // Map URL params to frontend types
  const typeMapping = {
    'sponsor-student': 'sponsor_student',
    'sponsorship-donation': 'sponsorship',
    'food-donation': 'food'
  };

  const initialDonationType = typeMapping[donationTypeFromUrl] || '';

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    pan_card: '',
    amount: '',
    donation_type: initialDonationType,
    purpose_id: '',
    food_item_id: '',
    meal_date: '',
    will_serve: '0',
    serving_members: '',
    arrival_time: '',
    special_notes: '',
    message: '',
    paymentMethod: 'online'
  });
  
  const [formStatus, setFormStatus] = useState({ 
    submitted: false, 
    success: false, 
    message: '',
    receiptUrl: null
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [showFullForm, setShowFullForm] = useState(false);
  const [donorData, setDonorData] = useState(null);
  const [isExistingDonor, setIsExistingDonor] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [amountError, setAmountError] = useState('');
  const [purposes, setPurposes] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const [showServingFields, setShowServingFields] = useState(false);
  const [websiteInfo, setWebsiteInfo] = useState(null);

  // Booking availability state
  const [bookedSlots, setBookedSlots] = useState({});
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [isDateAvailable, setIsDateAvailable] = useState(true);
  const [availabilityMessage, setAvailabilityMessage] = useState('');
  const [selectedMealName, setSelectedMealName] = useState('');
  const [toasts, setToasts] = useState([]);
  const [receiptData, setReceiptData] = useState(null);
  const [errors, setErrors] = useState({});
  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
          setRazorpayLoaded(true);
          resolve(true);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
          setRazorpayLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          setRazorpayLoaded(false);
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    loadRazorpayScript();
  }, []);

  // Fetch purpose master, food master, and website info
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [purposeResult, foodResult, websiteResult] = await Promise.all([
          apiService.fetchPurposeMaster(),
          apiService.fetchFoodMaster(),
          apiService.fetchWebinfo()
        ]);

        console.log('Website Result:', websiteResult);

        if (purposeResult.status === 'success') {
          setPurposes(purposeResult.data || []);
        }

        if (foodResult.status === 'success') {
          setFoodItems(foodResult.data || []);
          console.log('🍽️ Food Items loaded:', foodResult.data);
        }

        if (websiteResult.status === 'success' && websiteResult.data && websiteResult.data.length > 0) {
          setWebsiteInfo(websiteResult.data[0]);
        } else {
          setWebsiteInfo(null);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // ✅ Pre-fill form with iOS params when they exist
  useEffect(() => {
    if (!showIOSDetails) return;

    // Split full name into first_name / last_name
    const nameParts = (iosParams.donorName || '').trim().split(/\s+/);
    const first_name = nameParts[0] || '';
    const last_name = nameParts.slice(1).join(' ') || '';

    setFormData(prev => ({
      ...prev,
      first_name: prev.first_name || first_name,
      last_name: prev.last_name || last_name,
      email: prev.email || iosParams.donorEmail,
      phone: prev.phone || iosParams.donorPhone,
      address: prev.address || iosParams.donorAddress,
      pan_card: prev.pan_card || iosParams.donorPan,
      amount: prev.amount || iosParams.amount,
      purpose_id: prev.purpose_id || iosParams.purposeId,
      special_notes: prev.special_notes || iosParams.notes,
      donation_type: prev.donation_type || (iosParams.donationType === 'sponsor' ? 'sponsorship' : ''),
    }));

    if (iosParams.donorPhone) {
      setIsPhoneValid(true);
      setShowFullForm(true);
    }
  }, []);

  // Check donor when phone changes
  useEffect(() => {
    const checkDonor = async () => {
      if (isPhoneValid && formData.phone.length >= 10) {
        try {
          const result = await apiService.checkDonorByPhone(formData.phone);
          
          if (result.success && result.data && result.data.exists) {
            const donor = result.data.donor;
            setDonorData(donor);
            setIsExistingDonor(true);
            setFormData(prev => ({
              ...prev,
              first_name: donor.first_name || '',
              last_name: donor.last_name || '',
              email: donor.email || '',
              address: donor.address || '',
              pan_card: donor.pan_card || ''
            }));
            setPaymentError('');
          } else {
            setDonorData(null);
            setIsExistingDonor(false);
          }
        } catch (error) {
          console.error('Error checking donor:', error);
        }
      }
    };

    const timeoutId = setTimeout(checkDonor, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.phone, isPhoneValid]);

  // ============================================
  // LOAD BOOKED SLOTS - FOR FOOD DONATIONS
  // ============================================
  const loadBookedSlots = async (foodId) => {
    if (!foodId) {
      setBookedSlots({});
      return;
    }
    
    setCheckingAvailability(true);
    try {
      const response = await apiService.fetchFoodSponsorships();
      const slots = {};
      
      console.log('Food Sponsorships Response:', response);
      
      let bookings = [];
      if (response && response.status === 'success' && response.data) {
        bookings = Array.isArray(response.data) ? response.data : [response.data];
      }
      
      console.log('All bookings:', bookings);
      
      const confirmed = bookings.filter(b => {
        const status = (b.status || '').trim();
        return status === 'Confirmed';
      });
      
      console.log('Confirmed bookings:', confirmed);
      
      confirmed.forEach(booking => {
        if (booking.meal_date) {
          const date = booking.meal_date;
          if (!slots[date]) slots[date] = [];
          slots[date].push({
            id: booking.id,
            food_id: booking.food_id,
            status: booking.status,
            meal_date: booking.meal_date,
            meal_type: booking.meal_type || '',
          });
        }
      });
      
      console.log('Booked slots by date:', slots);
      setBookedSlots(slots);
      
    } catch (error) {
      console.error('Error loading booked slots:', error);
      setBookedSlots({});
    } finally {
      setCheckingAvailability(false);
    }
  };

  const checkDateAvailability = (date, mealId) => {
    if (!date || !mealId) {
      return { available: true, message: '' };
    }
    
    console.log('Checking availability for:', { date, mealId });
    console.log('Booked slots:', bookedSlots);
    
    const slots = bookedSlots[date] || [];
    
    console.log('Slots for date:', date, slots);
    
    const existingBooking = slots.find(booking => {
      const match = String(booking.food_id) === String(mealId);
      console.log(`Comparing food_id: ${booking.food_id} === ${mealId}? ${match}`);
      return match;
    });
    
    console.log('Existing booking found:', existingBooking);
    
    if (existingBooking) {
      const meal = foodItems.find(item => String(item.id) === String(mealId));
      const mealName = meal?.meal_name || selectedMealName || 'This meal';
      
      const dateObj = new Date(date + 'T00:00:00');
      const formattedDate = dateObj.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      
      return { 
        available: false, 
        message: `${mealName} is already booked for ${formattedDate}. Please choose another date.`
      };
    }
    
    const meal = foodItems.find(item => String(item.id) === String(mealId));
    const mealName = meal?.meal_name || selectedMealName || 'This meal';
    
    const dateObj = new Date(date + 'T00:00:00');
    const formattedDate = dateObj.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
    
    return { 
      available: true, 
      message: `✅ Available - You can book ${mealName} for ${formattedDate}` 
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'phone') {
      const isValid = value.length >= 10 && /^[0-9]{10,15}$/.test(value);
      setIsPhoneValid(isValid);
      setShowFullForm(isValid);
    }

    if (name === 'amount') {
      const numValue = parseFloat(value);
      if (value && (isNaN(numValue) || numValue < 1)) {
        setAmountError('Minimum amount is ₹1');
      } else {
        setAmountError('');
      }
    }

    if (name === 'will_serve') {
      setShowServingFields(value === '1');
    }
    if (name === 'food_item_id') {
      const selectedFood = foodItems.find(item => String(item.id) === String(value));
      console.log('Selected food from handleChange:', selectedFood);
      
      if (selectedFood) {
        const minQty = selectedFood.min_quantity || 1;
        const totalPrice = parseFloat(selectedFood.price_per_plate) * minQty;
        setFormData(prev => ({
          ...prev,
          food_item_id: value,
          amount: totalPrice.toString()
        }));
        setAmountError('');
        setSelectedMealName(selectedFood.meal_name || '');
        loadBookedSlots(value);
        setIsDateAvailable(true);
        setAvailabilityMessage('');
        setErrors(prev => ({ ...prev, meal_date: '' }));
      }
    }
    if (name === 'meal_date') {
      if (formData.food_item_id && value) {
        const result = checkDateAvailability(value, formData.food_item_id);
        setIsDateAvailable(result.available);
        setAvailabilityMessage(result.message);
        
        if (!result.available) {
          setErrors(prev => ({ ...prev, meal_date: result.message }));
        } else {
          setErrors(prev => ({ ...prev, meal_date: '' }));
        }
      } else {
        setIsDateAvailable(true);
        setAvailabilityMessage('');
        setErrors(prev => ({ ...prev, meal_date: '' }));
      }
    }
  };

  // Number to words function
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convert = (n) => {
      if (n < 20) return teens[n] || ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + convert(n % 1000) : '');
      if (n < 10000000) return convert(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + convert(n % 100000) : '');
      return convert(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + convert(n % 10000000) : '');
    };
    return convert(Math.round(num)) + ' Rupees Only';
  };

  // Toast function
  const showToast = (message, type = 'info') => {
    const id = Date.now() + Math.random() * 1000;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };

  const handleDonationTypeSelect = (type) => {
    setFormData(prev => ({
      ...prev,
      donation_type: type,
      purpose_id: '',
      food_item_id: '',
      meal_date: '',
      will_serve: '0',
      serving_members: '',
      arrival_time: '',
      special_notes: '',
      amount: ''
    }));
    setShowServingFields(false);
    setBookedSlots({});
    setIsDateAvailable(true);
    setAvailabilityMessage('');
    setSelectedMealName('');
  };

  // ============================================
  // HANDLE PAYMENT SUCCESS
  // ============================================
  const handlePaymentSuccess = async (response) => {
    try {
      setIsSubmitting(true);
      setPaymentError('');
      
      const verifyResult = await apiService.verifyRazorpayPayment({
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_order_id: response.razorpay_order_id,
        razorpay_signature: response.razorpay_signature
      });

      if (verifyResult.status) {
        const totalAmount = parseFloat(formData.amount);
        const donorNote = formData.message || '';

        let donorId = localStorage.getItem('donorId');
        if (!donorId && donorData) {
          donorId = donorData.id;
        }

        let donationType = '';
        let subType = '';
        let purposeText = '';
        let purposeId = null;

        if (formData.donation_type === 'sponsor_student') {
          donationType = 'student_sponsorship';
          subType = 'student_sponsorship';
          purposeText = 'Student Sponsorship';
          purposeId = null;
        } else if (formData.donation_type === 'sponsorship') {
          donationType = 'sponsor_donation';
          subType = 'sponsor';
          purposeText = purposes.find(p => p.id === parseInt(formData.purpose_id))?.purpose_name || 'Sponsorship Donation';
          purposeId = formData.purpose_id || null;
        } else if (formData.donation_type === 'food') {
          donationType = 'food_donation';
          subType = 'veg';
          const notesForPurpose = formData.special_notes || formData.message || '';
          purposeText = `Food Donation - ${selectedMealName || 'Meal'}${notesForPurpose ? ` - ${notesForPurpose}` : ''}`;
          purposeId = null;
        }

        const donationData = {
          donor_id: donorId,
          donation_type: donationType,
          sub_type: subType,
          purpose: purposeText,
          purpose_id: purposeId,
          amount: totalAmount,
          currency: 'INR',
          payment_method: 'online',
          transaction_id: response.razorpay_payment_id,
          payment_status: 'completed',
          payment_date: new Date().toISOString().split('T')[0],
          is_anonymous: 0,
          tax_exempted: formData.pan_card ? 1 : 0,
          pan_card: formData.pan_card?.toUpperCase() || '',
          status: 'approved',
          notes: JSON.stringify({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            donation_type: formData.donation_type,
            donor_notes: donorNote,
            food_item_id: formData.food_item_id || null,
            meal_date: formData.meal_date || null,
            will_serve: formData.will_serve || '0',
            serving_members: formData.serving_members || null,
            arrival_time: formData.arrival_time || null,
          }),
          user_id: 0,
          delete_status: 0
        };

        console.log('Saving donation with data:', donationData);

        const donationResponse = await apiService.saveData('donations', donationData);
        
        if (!donationResponse.success) {
          throw new Error(donationResponse.message || 'Failed to save donation record');
        }

        const donationId = donationResponse.data?.id;
        const savedDonationCode = donationResponse.data?.donation_code || '';

        if (formData.donation_type === 'food' && formData.food_item_id && donationId) {
          const foodSponsorshipData = {
            donation_id: donationId,
            donor_id: donorId,
            food_id: formData.food_item_id,
            meal_type: 'veg',
            meal_date: formData.meal_date,
            will_serve: formData.will_serve === '1' ? 1 : 0,
            serving_members: parseInt(formData.serving_members) || 0,
            arrival_time: formData.arrival_time || null,
            total_amount: totalAmount,
            special_notes: formData.special_notes || formData.message || '',
            status: 'confirmed',
            user_id: 0,
            delete_status: 0
          };
          await apiService.saveData('food_sponsorships', foodSponsorshipData);
        }

        if (formData.donation_type === 'sponsor_student' && donationId) {
          const studentSponsorshipData = {
            donation_id: donationId,
            donor_id: donorId,
            sponsorship_code: `SSP-${Date.now()}`,
            sponsorship_type: 'general',
            sponsorship_amount: totalAmount,
            start_date: new Date().toISOString().split('T')[0],
            status: 'active',
            user_id: 0,
            delete_status: 0
          };
          await apiService.saveData('student_sponsorships', studentSponsorshipData);
        }

        const receiptPayload = {
          donation_id: donationId,
          donor_id: donorId,
          receipt_type: formData.donation_type === 'sponsor_student' ? 'student_sponsor' :
                        formData.donation_type === 'sponsorship' ? 'sponsor' : 'food_donation',
          receipt_date: new Date().toISOString().split('T')[0],
          amount: totalAmount,
          currency: 'INR',
          pan_number: formData.pan_card?.toUpperCase() || '',
          phone: formData.phone || '',
          organization_name: formData.first_name + ' ' + formData.last_name || '',
          organization_address: formData.address || '',
          tax_exemption_certificate: formData.pan_card ? 'Yes' : 'No',
          additional_details: JSON.stringify({
            donation_code: savedDonationCode,
            donation_type: donationType,
            purpose: purposeText,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            donor_notes: donorNote,
          }),
          user_id: 0,
          delete_status: 0
        };

        const receiptResponse = await apiService.saveData('receipts', receiptPayload);
        const savedReceiptNumber = receiptResponse.data?.receipt_number || `RCP-${Date.now()}`;
        const receiptId = receiptResponse.data?.id;

        const typeLabels = {
          'sponsor_student': 'Student Sponsorship',
          'sponsorship': 'Sponsorship Donation',
          'food': 'Food Donation'
        };

        setReceiptData({
          receiptNumber: savedReceiptNumber,
          date: new Date().toISOString().split('T')[0],
          donorName: formData.first_name + ' ' + formData.last_name || '',
          donorAddress: formData.address || '',
          donorPhone: formData.phone || '',
          donorPan: formData.pan_card || '',
          amount: totalAmount,
          amountInWords: numberToWords(totalAmount),
          paymentMode: 'Online (Razorpay)',
          purpose: typeLabels[formData.donation_type] || 'Donation',
          notes: donorNote,
          donationId: donationId,
          receiptId: receiptId,
          donationCode: savedDonationCode,
          paymentId: response.razorpay_payment_id,
        });

        setFormStatus({
          submitted: true,
          success: true,
          message: 'Thank you for your generous donation!',
          receiptUrl: getReceiptUrl(receiptId),
          donationId: donationId,
          receiptId: receiptId,
          amount: totalAmount,
          receiptNumber: savedReceiptNumber,
          donorName: formData.first_name + ' ' + formData.last_name,
          paymentMethod: 'Online',
          purpose: typeLabels[formData.donation_type] || 'Donation'
        });

        showToast(`Donation successful! Receipt: ${savedReceiptNumber}`, 'success');

      } else {
        throw new Error(verifyResult.message || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setPaymentError(error.message || 'Payment verification failed. Please contact support.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentFailure = (response) => {
    console.error('Payment failed:', response);
    setPaymentError('Payment failed. Please try again or use another payment method.');
    setIsSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPaymentError('');
    setIsSubmitting(true);

    try {
      if (!formData.phone || formData.phone.length < 10) {
        setPaymentError('Please enter a valid phone number');
        setIsSubmitting(false);
        return;
      }

      if (!formData.first_name || formData.first_name.trim().length < 2) {
        setPaymentError('Please enter your first name');
        setIsSubmitting(false);
        return;
      }

      if (formData.email && !formData.email.includes('@')) {
        setPaymentError('Please enter a valid email address');
        setIsSubmitting(false);
        return;
      }

      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount < 1) {
        setPaymentError('Please enter a valid amount (minimum ₹1)');
        setIsSubmitting(false);
        return;
      }

      if (formData.donation_type === 'food' && formData.meal_date && formData.food_item_id) {
        const result = checkDateAvailability(formData.meal_date, formData.food_item_id);
        if (!result.available) {
          setPaymentError(result.message);
          setIsSubmitting(false);
          return;
        }
      }

      if (!razorpayLoaded || typeof window.Razorpay === 'undefined') {
        setPaymentError('Payment system is loading. Please try again in a moment.');
        setIsSubmitting(false);
        return;
      }

      if (formData.donation_type === 'sponsorship' && !formData.purpose_id) {
        setPaymentError('Please select a purpose for sponsorship donation');
        setIsSubmitting(false);
        return;
      }

      if (formData.donation_type === 'food' && !formData.food_item_id) {
        setPaymentError('Please select a meal for food donation');
        setIsSubmitting(false);
        return;
      }

      if (formData.donation_type === 'food' && !formData.meal_date) {
        setPaymentError('Please select a meal date');
        setIsSubmitting(false);
        return;
      }

      const getBackendDonationType = (type) => {
        const mapping = {
          'sponsor_student': 'sponsor',
          'sponsorship': 'sponsor',
          'food': 'food'
        };
        return mapping[type] || 'sponsor';
      };

      const getPurposeName = () => {
        if (formData.donation_type === 'sponsor_student') {
          return 'Student Sponsorship';
        }
        if (formData.donation_type === 'sponsorship') {
          const purpose = purposes.find(p => p.id === parseInt(formData.purpose_id));
          return purpose?.purpose_name || 'Sponsorship Donation';
        }
        if (formData.donation_type === 'food') {
          const food = foodItems.find(f => String(f.id) === String(formData.food_item_id));
          return `Food Donation - ${food?.meal_name || 'Meal'}`;
        }
        return 'Donation';
      };

      const orderData = await apiService.createRazorpayOrder({
        amount: amount,
        donation_type: getBackendDonationType(formData.donation_type),
        donor_id: donorData?.id || '',
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        pan_card: formData.pan_card,
        address: formData.address,
        purpose: getPurposeName(),
        purpose_id: formData.purpose_id || '',
        meal_id: formData.food_item_id || '',
        meal_date: formData.meal_date || '',
        special_notes: formData.special_notes || '',
        will_serve: formData.will_serve || '0',
        serving_members: formData.serving_members || '',
        arrival_time: formData.arrival_time || '',
      });

      if (!orderData.status) {
        throw new Error(orderData.message || 'Failed to create payment order');
      }

      const typeLabels = {
        'sponsor_student': 'Sponsor Student',
        'sponsorship': 'Sponsorship Donation',
        'food': 'Food Donation'
      };

      const razorpayOptions = {
        key: 'rzp_live_TAV2geqqZfdp9D',
        amount: orderData.amount,
        currency: 'INR',
        order_id: orderData.order_id,
        name: 'Devnar Foundation',
        description: `Donation: ${typeLabels[formData.donation_type] || 'Donation'}`,
        image: '/images/devnar-logo.png',
        prefill: {
          name: formData.first_name + ' ' + formData.last_name,
          email: formData.email,
          contact: formData.phone
        },
        notes: {
          donation_type: formData.donation_type,
          donor_id: donorData?.id || '',
          first_name: formData.first_name,
          last_name: formData.last_name,
          pan_card: formData.pan_card,
          purpose_id: formData.purpose_id || '',
          meal_id: formData.food_item_id || '',
          meal_date: formData.meal_date || '',
        },
        theme: {
          color: '#E3A857'
        },
        modal: {
          ondismiss: function() {
            setIsSubmitting(false);
            setPaymentError('Payment cancelled. Please try again.');
          }
        },
        handler: function(response) {
          handlePaymentSuccess(response);
        }
      };

      const razorpay = new window.Razorpay(razorpayOptions);
      
      razorpay.on('payment.failed', function(response) {
        handlePaymentFailure(response);
      });

      razorpay.open();

    } catch (error) {
      console.error('Payment initialization error:', error);
      setPaymentError(error.message || 'Failed to initialize payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormStatus({ submitted: false, success: false, message: '', receiptUrl: null });
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: '',
      pan_card: '',
      amount: '',
      donation_type: '',
      purpose_id: '',
      food_item_id: '',
      meal_date: '',
      will_serve: '0',
      serving_members: '',
      arrival_time: '',
      special_notes: '',
      message: '',
      paymentMethod: 'online'
    });
    setDonorData(null);
    setIsExistingDonor(false);
    setShowFullForm(false);
    setIsPhoneValid(false);
    setPaymentError('');
    setAmountError('');
    setShowServingFields(false);
    setBookedSlots({});
    setIsDateAvailable(true);
    setAvailabilityMessage('');
    setSelectedMealName('');
    setReceiptData(null);
  };

  const getDonationTypeLabel = (type) => {
    const labels = {
      'sponsor_student': 'Sponsor Student',
      'sponsorship': 'Sponsorship Donation',
      'food': 'Food Donation'
    };
    return labels[type] || type;
  };

  const hasBankDetails = () => {
    if (!websiteInfo) return false;
    return !!(websiteInfo.bank_account_name || websiteInfo.bank_account_number || websiteInfo.bank_name);
  };

  // --- IMPACT_STATS ---
  const IMPACT_STATS = [
    { value: '500+', label: 'Students Supported', icon: 'fa-users' },
    { value: '36+', label: 'Years of Service', icon: 'fa-calendar-alt' },
    { value: '100%', label: 'Free Education', icon: 'fa-book-open' },
    { value: '670+', label: 'Boarding Facilities', icon: 'fa-home' }
  ];

  // --- WAYS_TO_HELP ---
  const WAYS_TO_HELP = [
    {
      title: 'Sponsor Student',
      desc: 'Support a visually impaired student\'s education, including tuition, books, and daily needs.',
      href: '/donate?type=sponsor-student',
      icon: 'fa-graduation-cap'
    },
    {
      title: 'Sponsorship Donation',
      desc: 'Make a sponsorship donation to support our programs and infrastructure for the blind.',
      href: '/donate?type=sponsorship-donation',
      icon: 'fa-hand-holding-heart'
    },
    {
      title: 'Food Donation',
      desc: 'Provide nutritious meals to our students and help us maintain the mid-day meal program.',
      href: '/donate?type=food-donation',
      icon: 'fa-utensils'
    }
  ];

  return (
    <>
      <Header />
      <div className="donate-page devnar">
        <a className="skip-link" href="#main-donate">Skip to main content</a>

        <main id="main-donate" className="wrap page-content-donate">

          {/* --- Donation Form --- */}
          <section id="donate-form" className="donation-form-section">
            <div className="section-title">
              <h2>Support Us</h2>
              <div className="rule" />
            </div>

            {/* ✅ iOS Details Card — only shows when params are passed */}
            {showIOSDetails && (
              <div className="ios-details-card">
                <h3 className="ios-details-title">Donation Details</h3>
                <div className="ios-details-grid">
                  <div className="ios-detail-row">
                    <span className="ios-label">Donor Name</span>
                    <span className="ios-value">{iosParams.donorName || '—'}</span>
                  </div>
                  <div className="ios-detail-row">
                    <span className="ios-label">Phone</span>
                    <span className="ios-value">{iosParams.donorPhone || '—'}</span>
                  </div>
                  {iosParams.donorEmail && (
                    <div className="ios-detail-row">
                      <span className="ios-label">Email</span>
                      <span className="ios-value">{iosParams.donorEmail}</span>
                    </div>
                  )}
                  {iosParams.donorAddress && (
                    <div className="ios-detail-row">
                      <span className="ios-label">Address</span>
                      <span className="ios-value">{iosParams.donorAddress}</span>
                    </div>
                  )}
                  {iosParams.donorPan && (
                    <div className="ios-detail-row">
                      <span className="ios-label">PAN Card</span>
                      <span className="ios-value">{iosParams.donorPan}</span>
                    </div>
                  )}
                  {iosParams.donorOrganization && (
                    <div className="ios-detail-row">
                      <span className="ios-label">Organization</span>
                      <span className="ios-value">{iosParams.donorOrganization}</span>
                    </div>
                  )}
                  <div className="ios-detail-row">
                    <span className="ios-label">Purpose</span>
                    <span className="ios-value">{iosParams.purpose || '—'}</span>
                  </div>
                  {iosParams.notes && (
                    <div className="ios-detail-row ios-detail-full">
                      <span className="ios-label">Note</span>
                      <span className="ios-value">{iosParams.notes}</span>
                    </div>
                  )}
                  <div className="ios-detail-row ios-detail-total">
                    <span className="ios-label">Total Amount</span>
                    <span className="ios-value">
                      ₹{parseFloat(iosParams.amount || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                </div>
              </div>
            )}

          </section>

        </main>
      </div>
      <Footer />
    </>
  );
}